import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, ArrowRight, Expand, ZoomIn, Minimize2 } from "lucide-react";
import { useLang } from "../lib/i18n";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./ui/dialog";

const images = [
  { src: "/images/business/platform.png", width: 1536, height: 1024 },
  { src: "/images/business/academy.png", width: 1024, height: 1536 },
  { src: "/images/business/restaurant.png", width: 1024, height: 1536 },
  { src: "/images/business/booking.png", width: 1536, height: 1024 },
  { src: "/images/business/performance.png", width: 1024, height: 1536 },
  { src: "/images/business/personal-qr.png", width: 1024, height: 1536 },
  { src: "/images/39c3da4d12d3.png", width: 1024, height: 1536 },
  { src: "/images/business/vision.png", width: 1024, height: 1536 },
];

export const businessStoryCopy = {
  en: {
    title: "The business, in pictures",
    intro: "Explore the concept — from training artists to creating value for venues, guests and partners.",
    open: "View full image", previous: "Previous image", next: "Next image", zoom: "Zoom in", fit: "Fit to screen",
    cards: [
      ["The platform", "Restaurants find talent. Artists access paid opportunities combining performance and hospitality service."],
      ["The academy", "Artists learn service, presentation, punctuality and food-handling basics before working in a venue."],
      ["The restaurant", "Trained performers welcome guests, support table service and create entertainment as part of the same experience."],
      ["The booking", "Each shift brings location, timings, dress code, performance guidelines and service duties into one brief."],
      ["The performance", "Live entertainment gives guests an experience to film and share, creating visibility for artists and venues."],
      ["The personal QR", "Guests can discover the artist, browse the menu, follow social profiles and leave digital tips."],
      ["Value for everyone", "Artists gain earning opportunities; venues gain visibility and engagement; sponsors reach guests through digital artist pages."],
      ["The vision", "A new profession: hospitality entertainers who serve, perform, create content and turn a meal into a memorable experience."],
    ],
  },
  it: {
    title: "Il business, per immagini",
    intro: "Esplora il concept: dalla formazione degli artisti al valore per locali, clienti e partner.",
    open: "Ingrandisci l’immagine", previous: "Immagine precedente", next: "Immagine successiva", zoom: "Ingrandisci", fit: "Adatta allo schermo",
    cards: [
      ["La piattaforma", "I ristoranti trovano talenti. Gli artisti accedono a opportunità retribuite che uniscono esibizione e servizio nell’ospitalità."],
      ["L’Academy", "Prima di lavorare in un locale, gli artisti apprendono servizio, presentazione, puntualità e nozioni di igiene alimentare."],
      ["Il ristorante", "I performer formati accolgono i clienti, collaborano al servizio ai tavoli e creano intrattenimento nella stessa esperienza."],
      ["La prenotazione del turno", "Ogni incarico riunisce luogo, orari, abbigliamento, indicazioni per lo spettacolo e mansioni di servizio."],
      ["L’esibizione", "L’intrattenimento dal vivo offre ai clienti un’esperienza da filmare e condividere, dando visibilità ad artisti e locali."],
      ["Il QR personale", "I clienti possono scoprire l’artista, consultare il menu, seguire i suoi profili social e lasciare mance digitali."],
      ["Valore per tutti", "Gli artisti ottengono opportunità di guadagno; i locali visibilità e coinvolgimento; gli sponsor raggiungono i clienti tramite le pagine digitali degli artisti."],
      ["La visione", "Una nuova professione: hospitality entertainer capaci di servire, esibirsi, creare contenuti e trasformare un pasto in un’esperienza da ricordare."],
    ],
  },
  es: {
    title: "El negocio, en imágenes",
    intro: "Explora el concepto: desde la formación de artistas hasta el valor para locales, clientes y socios.",
    open: "Ampliar la imagen", previous: "Imagen anterior", next: "Imagen siguiente", zoom: "Ampliar", fit: "Ajustar a la pantalla",
    cards: [
      ["La plataforma", "Los restaurantes encuentran talento. Los artistas acceden a oportunidades remuneradas que combinan actuación y servicio de hostelería."],
      ["La Academia", "Antes de trabajar en un local, los artistas aprenden servicio, presentación, puntualidad y nociones de higiene alimentaria."],
      ["El restaurante", "Los artistas formados reciben a los clientes, apoyan el servicio de mesas y ofrecen entretenimiento en una misma experiencia."],
      ["La reserva del turno", "Cada encargo reúne lugar, horarios, vestimenta, indicaciones para la actuación y tareas de servicio."],
      ["La actuación", "El entretenimiento en vivo ofrece a los clientes una experiencia para grabar y compartir, dando visibilidad a artistas y locales."],
      ["El QR personal", "Los clientes pueden descubrir al artista, consultar el menú, seguir sus perfiles sociales y dejar propinas digitales."],
      ["Valor para todos", "Los artistas ganan oportunidades de ingresos; los locales, visibilidad e interacción; los patrocinadores llegan a los clientes mediante las páginas digitales de los artistas."],
      ["La visión", "Una nueva profesión: artistas de la hostelería que sirven, actúan, crean contenido y convierten una comida en una experiencia memorable."],
    ],
  },
};

export default function BusinessStory() {
  const { lang } = useLang();
  const copy = businessStoryCopy[lang] || businessStoryCopy.en;
  const [selected, setSelected] = useState(null);
  const [zoomed, setZoomed] = useState(false);
  const sectionRef = useRef(null);
  const openerRef = useRef(null);
  useEffect(() => {
    if (window.location.hash === "#business-in-pictures") {
      const frame = requestAnimationFrame(() => sectionRef.current?.scrollIntoView());
      return () => cancelAnimationFrame(frame);
    }
  }, []);
  const activeImage = selected === null ? null : images[selected];
  const activeCopy = selected === null ? null : copy.cards[selected];
  const openImage = (index) => {
    if (selected === null) openerRef.current = document.activeElement;
    setZoomed(false);
    setSelected(index);
  };
  const navigate = (offset) => openImage((selected + offset + images.length) % images.length);

  return (
    <section ref={sectionRef} id="business-in-pictures" aria-labelledby="business-story-title" className="scroll-mt-24 border-b border-white/10 bg-[#050505]">
      <div className="max-w-6xl mx-auto px-6 py-14 sm:py-20">
        <h2 id="business-story-title" className="font-serif-lux text-4xl sm:text-5xl font-light">{copy.title}</h2>
        <p className="text-base sm:text-lg text-neutral-300 leading-relaxed mt-4 mb-10 max-w-3xl">{copy.intro}</p>
        <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
          {images.map((image, index) => (
            <figure key={image.src} className="card-lux rounded-sm overflow-hidden flex flex-col">
              <button type="button" onClick={() => openImage(index)} aria-label={`${copy.open}: ${copy.cards[index][0]}`} className="group relative block w-full bg-black focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-[-4px] focus-visible:outline-[#D4AF37]">
                <img src={image.src} alt={copy.cards[index][1]} width={image.width} height={image.height} loading="lazy" decoding="async" className="block w-full h-auto object-contain" />
                <span className="absolute top-3 right-3 w-10 h-10 bg-black/85 border border-gold/50 rounded-full flex items-center justify-center gold group-hover:bg-gold group-hover:text-black transition-colors" aria-hidden="true"><Expand size={18} /></span>
              </button>
              <figcaption className="p-5 sm:p-6 border-t border-white/10 mt-auto">
                <h3 className="font-serif-lux text-2xl sm:text-3xl gold">{copy.cards[index][0]}</h3>
                <p className="text-base text-neutral-300 leading-relaxed mt-3">{copy.cards[index][1]}</p>
                <button type="button" onClick={() => openImage(index)} className="inline-flex items-center gap-2 text-sm gold underline underline-offset-4 mt-4 py-1">{copy.open}<Expand size={14} aria-hidden="true" /></button>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
      <Dialog open={selected !== null} onOpenChange={(open) => { if (!open) { setSelected(null); setZoomed(false); } }}>
        <DialogContent className="w-[calc(100vw-1rem)] max-w-6xl h-[calc(100dvh-2rem)] flex flex-col gap-0 p-0 overflow-hidden bg-[#080808] border-gold/30" onCloseAutoFocus={(event) => {
          event.preventDefault();
          openerRef.current?.focus();
        }} onKeyDown={(event) => {
          if (event.key === "ArrowRight") { event.preventDefault(); navigate(1); }
          if (event.key === "ArrowLeft") { event.preventDefault(); navigate(-1); }
        }}>
          {activeImage && <>
            <div className="px-4 sm:px-6 py-4 pr-12 border-b border-white/10 shrink-0">
              <DialogTitle className="font-serif-lux text-2xl gold">{activeCopy[0]}</DialogTitle>
              <DialogDescription className="sr-only">{activeCopy[1]}</DialogDescription>
            </div>
            <div key={`${selected}-${zoomed}`} className="flex-1 min-h-0 overflow-auto overscroll-contain bg-black" tabIndex={0} aria-label={activeCopy[0]}>
              <div className="min-w-full min-h-full w-max flex items-center justify-center">
                <img src={activeImage.src} alt={activeCopy[1]} width={activeImage.width} height={activeImage.height} className="block object-contain" style={zoomed ? { width: activeImage.width, maxWidth: "none", height: "auto" } : { width: "auto", height: "auto", maxWidth: "min(calc(100vw - 3rem), 1100px)", maxHeight: "calc(100dvh - 13rem)" }} />
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-3 px-4 sm:px-6 py-3 border-t border-white/10 shrink-0">
              <div className="flex items-center gap-3">
                <Button type="button" variant="outline" size="icon" onClick={() => navigate(-1)} aria-label={copy.previous}><ArrowLeft size={18} /></Button>
                <span className="text-sm text-neutral-300 tabular-nums" aria-live="polite">{selected + 1} / {images.length}</span>
                <Button type="button" variant="outline" size="icon" onClick={() => navigate(1)} aria-label={copy.next}><ArrowRight size={18} /></Button>
              </div>
              <Button type="button" variant="outline" onClick={() => setZoomed(!zoomed)} aria-pressed={zoomed} className="text-sm gap-2">
                {zoomed ? <Minimize2 size={17} /> : <ZoomIn size={17} />} {zoomed ? copy.fit : copy.zoom}
              </Button>
            </div>
          </>}
        </DialogContent>
      </Dialog>
    </section>
  );
}
